import { MerchantValidationError } from "../error";
import * as AddressFields from "../fields/address";
import * as OwnerFields from "../fields/beneficialOwners";
import { FieldSet } from "../fieldset";
import { Merchant } from "../merchant";
import { OwnerFieldControlValues, VerificationMethod } from "../types";

export type BusinessOwnerValueMap = {
  certifiedGovIdPrimary: string;
  certifiedGovIdSecondary: string;
  city: string;
  dob: string;
  firstName: string;
  govId: string;
  govIdBackside: string;
  lastName: string;
  postalCode: string;
  ssn: string;
  state: string;
  street: string;
  street2: string;
  verificationMethod: string;
};

export type BusinessOwnerValueMapper<T> = {
  [K in keyof BusinessOwnerValueMap]: T;
};

interface BusinessOwnerOpts {
  controlValues: OwnerFieldControlValues;
  ownerIndex: number;
}

export class BusinessOwner extends FieldSet<BusinessOwnerValueMap> {
  static fromJson(
    Owner: BusinessOwner,
    {
      certifiedGovIdPrimary,
      certifiedGovIdSecondary,
      city,
      dob,
      firstName,
      govId,
      govIdBackside,
      lastName,
      postalCode,
      ssn,
      state,
      street,
      street2,
      verificationMethod,
    }: BusinessOwnerValueMap
  ): BusinessOwner {
    Owner.certifiedGovIdPrimary.value = certifiedGovIdPrimary;
    Owner.certifiedGovIdSecondary.value = certifiedGovIdSecondary;
    Owner.city.value = city;
    Owner.dob.value = dob;
    Owner.firstName.value = firstName;
    Owner.govId.value = govId;
    Owner.govIdBackside.value = govIdBackside;
    Owner.lastName.value = lastName;
    Owner.postalCode.value = postalCode;
    Owner.ssn.value = ssn;
    Owner.state.value = state;
    Owner.street.value = street;
    Owner.street2.value = street2;
    Owner.verificationMethod.value = verificationMethod as VerificationMethod;
    return Owner;
  }

  public certifiedGovIdPrimary: OwnerFields.CertifiedGovIdPrimaryField;
  public certifiedGovIdSecondary: OwnerFields.CertifiedGovIdSecondaryField;
  public city: AddressFields.CityField;
  public dob: OwnerFields.DobField;
  public firstName: OwnerFields.FirstNameField;
  public govId: OwnerFields.GovIdField;
  public govIdBackside: OwnerFields.GovIdBacksideField;
  public lastName: OwnerFields.LastNameField;
  public postalCode: AddressFields.PostalCodeField;
  public ssn: OwnerFields.SsnField;
  public state: AddressFields.StateField;
  public street: AddressFields.StreetField;
  public street2: AddressFields.Street2Field;
  public verificationMethod: OwnerFields.VerificationMethodField;

  public controlValues: OwnerFieldControlValues;
  public ownerIndex: number;

  constructor(model: Merchant, opts: BusinessOwnerOpts) {
    super();
    this.ownerIndex = opts.ownerIndex;
    this.controlValues = opts.controlValues;
    this.fieldSetName = `Owner${opts.ownerIndex}FieldSet`;

    this.certifiedGovIdPrimary = new OwnerFields.CertifiedGovIdPrimaryField(
      model,
      this
    );
    this.certifiedGovIdSecondary = new OwnerFields.CertifiedGovIdSecondaryField(
      model,
      this
    );
    this.city = new AddressFields.CityField(model, this, {
      propertyName: "city",
    });
    this.dob = new OwnerFields.DobField(model, this);
    this.firstName = new OwnerFields.FirstNameField(model, this);
    this.govId = new OwnerFields.GovIdField(model, this);
    this.govIdBackside = new OwnerFields.GovIdBacksideField(model, this);
    this.lastName = new OwnerFields.LastNameField(model, this);
    this.postalCode = new AddressFields.PostalCodeField(model, this, {
      propertyName: "postalCode",
    });
    this.ssn = new OwnerFields.SsnField(model, this);
    this.state = new AddressFields.StateField(model, this, {
      propertyName: "state",
    });
    this.street = new AddressFields.StreetField(model, this, {
      propertyName: "street",
    });
    this.street2 = new AddressFields.Street2Field(model, this, {
      propertyName: "street2",
    });
    this.verificationMethod = new OwnerFields.VerificationMethodField(
      model,
      this
    );
    this.fields = [
      this.certifiedGovIdPrimary,
      this.certifiedGovIdSecondary,
      this.city,
      this.dob,
      this.firstName,
      this.govId,
      this.govIdBackside,
      this.lastName,
      this.postalCode,
      this.ssn,
      this.state,
      this.street,
      this.street2,
      this.verificationMethod,
    ];
  }

  getApplicableValue(): Partial<BusinessOwnerValueMap> {
    return this.fields
      .filter((f) => f.applicable)
      .reduce((acc, f) => {
        acc[f.propertyName as keyof BusinessOwnerValueMap] = f.value ?? "";
        return acc;
      }, {} as Partial<BusinessOwnerValueMap>);
  }

  validate({ stringify = true }: { stringify: boolean }): void {
    const Errors = new MerchantValidationError();

    let errorCount = 0;
    for (const field of this.fields) {
      try {
        field.validate();
      } catch (ex) {
        errorCount++;
        if (
          Boolean(Errors.fieldErrorMap.owners) &&
          Array.isArray(Errors.fieldErrorMap.owners)
        ) {
          Errors.fieldErrorMap.owners[this.ownerIndex][
            field.propertyName as keyof BusinessOwnerValueMap
          ] = { error: stringify ? ex.message : ex };
        }
      }
    }

    if (errorCount) {
      throw Errors.fieldErrorMap;
    }
  }
}
